import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';

import QuizBuilder from './QuizBuilder';
import QuizReviewModal from '@/common/components/modals/custom/content/QuizReviewModal';
import Button from '@/common/components/ui/Button';

import QuizBuilderService from '@/services/course/creation/quiz-builder-service';
import { QuizQuestionData } from 'quiz-types';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { AIIcon } from '@/assets/icons/button';

interface Props {
  visible: boolean;
  cancelCreate: () => void;
  isEditing: boolean;
}

function QuizBuilderContainer({ visible, cancelCreate, isEditing }: Props): React.JSX.Element {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const questions: Array<QuizQuestionData> = useAppSelector(() => store.getState().quiz.questions);

  const [isReviewVisible, setReviewVisible] = useState<boolean>(false);
  const [baselineQuestions, setBaselineQuestions] = useState<Array<QuizQuestionData> | null>(null);

  // Capture baseline questions once when editing begins and the container is visible
  useEffect(() => {
    if (visible && isEditing && baselineQuestions === null) {
      // Deep copy to avoid later mutations
      setBaselineQuestions(JSON.parse(JSON.stringify(questions)) as Array<QuizQuestionData>);
    }
  }, [visible, isEditing, baselineQuestions, questions]);

  const handleReviewCloseSuccess = (): void => {
    setReviewVisible(false);
    cancelCreate();
  };

  const handleClose = () => {
    setReviewVisible(false);
  };

  const handleReviewOpen = () => {
    if (questions.length === 0 || !QuizBuilderService.isQuizReadyToSubmit(questions)) {
      toast.error('Please complete all questions before creating quiz.');
      return;
    }
    setReviewVisible(true);
  };

  const handleGenerateQuizOpen = () => {
    // Open modal
    dispatch(setModalVisible(true));
    dispatch(setModalName('GenerateQuiz'));

    // if editing, open warn modal
  };

  if (!visible) {
    return <></>;
  }

  return (
    <div className='flex flex-col w-full h-full gap-2'>
      <div className='buttons-container flex items-center justify-between py-4 gap-2'>
        <div className='flex gap-2'>
          <Button outline={true} onClick={cancelCreate}>
            Cancel
          </Button>
          <Button onClick={handleReviewOpen} className='text-nowrap'>
            {isEditing ? 'Update Quiz' : 'Create Quiz'}
          </Button>
        </div>
        <Button onClick={handleGenerateQuizOpen} className='text-nowrap'>
          <AIIcon className='text-text_contrast w-[20px] h-[20px] mr-2' /> {`Generate Quiz`}
        </Button>
      </div>
      <QuizBuilder />
      <QuizReviewModal
        visible={isReviewVisible}
        onClose={handleClose}
        onReviewSuccess={handleReviewCloseSuccess}
        isEditing={isEditing}
        baselineQuestions={baselineQuestions}
      />
    </div>
  );
}

export default QuizBuilderContainer;
// TODO: make questions reorder enabled
